import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryRegistryWithReauth, baseQueryRouterWithReauth } from './baseQuery';
import { getCurrentProjectId } from '../../common/context/project-storage';
import { getRouterApiKey } from '../../common/context/router-api-key-storage';
import { routerApiPathPrefix, routerServiceURL } from './defines';
// Note: Import from verna-promptlab-lib when available
// For now, define types locally
export interface PromptSpec {
  promptId: string;
  name: string;
  schemaVersion: string;
  inputs?: Array<{ name: string; type: string; required?: boolean; description?: string }>;
  template: Array<{ role: string; content: string }>;
  outputDefinition: {
    outputFormat: string;
    jsonSchema?: Record<string, any>;
    /** Repair JSON truncated by the model output limit before returning it. */
    enforceJsonStructure?: boolean;
    description?: string;
  };
  constraints?: any;
  [key: string]: any;
}

export interface ExecutionRequest {
  promptId?: string;
  version?: string;
  prompt?: PromptSpec | { promptId: string; version?: string } | string;
  inputs?: Record<string, any>;
  modelPolicy?: {
    primary: { providerType: string; model: string };
  };
  options?: any;
  context?: any;
  trace?: any;
}

export interface ExecutionResult {
  outputText?: string;
  outputJson?: any;
  usage: { promptTokens: number; completionTokens: number; totalTokens?: number };
  cost?: { totalCost: number; currency: string };
  provider: { providerType: string; model: string };
  timing: { startedAt: string; endedAt: string; latencyMs: number };
  eval?: any;
}

// ─── Health semaphore types ───────────────────────────────────────────────────

export interface HealthIssue {
  code: string;
  severity: 'alert' | 'warning';
  message: string;
}

export interface HealthResult {
  status: 'alert' | 'warning' | 'ok';
  issues: HealthIssue[];
}

// Types
export type PromptStatus = 'active' | 'paused' | 'inactive';

export interface PromptListItem {
  id: string;
  promptId: string;
  name: string;
  description?: string;
  intent?: string;
  tags: string[];
  modelCode?: string;
  fallbackModelCodes?: string[];
  activeVersion?: string;
  outputTemplate?: string;
  modelParameters?: Record<string, any>;
  sourceScenarioId?: string;
  sourceExecutionGroupId?: string;
  /**
   * Lifecycle status. Only `active` is executable. Legacy prompts may omit
   * this field — treat undefined as `active`. See registry-service
   * `prompt.schema.ts` and router-service execution gate.
   */
  status?: PromptStatus;
  createdAt: string;
  updatedAt: string;
  health?: HealthResult;
}

export interface PromptVersion {
  id: string;
  promptId: string;
  version: string;
  isActive?: boolean;
  spec: PromptSpec;
  createdBy?: string;
  changelog?: string;
  sourceScenarioId?: string;
  sourceExecutionGroupId?: string;
  createdAt: string;
}

// ─── Flow types (Phase 1) ────────────────────────────────────────────────

export type FlowStatusType = 'active' | 'paused' | 'inactive';

export interface FlowListItem {
  id: string;
  flowId: string;
  name: string;
  description?: string;
  tags?: string[];
  activeVersion?: string;
  status?: FlowStatusType;
  createdAt: string;
  updatedAt: string;
}

/**
 * FlowSpec mirrors the lib's `FlowSpec` shape but is duplicated here so
 * the frontend doesn't pull in the lib runtime. Kept narrow to what the
 * UI needs.
 */
/**
 * Per-step error-handling policy. Matches the lib's `ErrorPolicy` union.
 * Lite copy here to avoid a hard dep on the lib from the frontend bundle.
 */
export type FlowErrorPolicyLite =
  | 'fail'
  | 'continue'
  | { type: 'retry'; maxAttempts: number; backoffMs?: number; retryOnCodes?: string[] };

/**
 * Single step in a FlowSpec (frontend lite view). Discriminated by `type`.
 * Phase 1 shipped `prompt` only; Phase 2 adds `parallel`/`map`/`transform`
 * — fields per-type are optional here so old prompt-only flows still
 * type-check, and the visual editor branches on `type` for rendering.
 */
export interface FlowStepLite {
  id: string;
  name?: string;
  type: 'prompt' | 'parallel' | 'map' | 'transform' | 'branch' | 'subflow' | 'loop';
  inputBindings?: Record<string, string>;
  outputBinding?: string;
  timeoutMs?: number;
  cacheable?: boolean;
  errorPolicy?: FlowErrorPolicyLite;
  // — prompt
  promptId?: string;
  promptVersion?: string;
  modelOverride?: { modelCode?: string; fallbackModelCodes?: string[]; modelParameters?: Record<string, any> };
  // — parallel
  children?: FlowStepLite[];
  maxConcurrency?: number;
  // — map
  itemsBinding?: string;
  itemAs?: string;
  step?: FlowStepLite;
  // — transform
  expression?: string;
}

export interface FlowInputLite {
  name: string;
  type: string;
  required?: boolean;
  description?: string;
  default?: any;
}

/**
 * Declarative output contract for a flow, mirroring a prompt's
 * `outputDefinition`. Stored as a first-class field on the flow spec; the
 * router surfaces it on `GET /flows/:flowId` and infers it from
 * `outputBindings` when absent. Descriptive only — not enforced at run time.
 */
export interface FlowOutputDefinitionLite {
  outputFormat: 'text' | 'json';
  jsonSchema?: Record<string, any>;
  description?: string;
  /** True when the router derived this from outputBindings (no explicit declaration). */
  inferred?: boolean;
}

export interface FlowSpecLite {
  flowId: string;
  name: string;
  description?: string;
  tags?: string[];
  schemaVersion: string;
  inputs: FlowInputLite[];
  steps: FlowStepLite[];
  outputBindings?: Record<string, string>;
  outputDefinition?: FlowOutputDefinitionLite;
  metadata?: Record<string, any>;
}

export interface FlowVersionRecord {
  id: string;
  flowId: string;
  version: string;
  isActive?: boolean;
  spec: FlowSpecLite;
  createdBy?: string;
  changelog?: string;
  createdAt: string;
  updatedAt: string;
}

export interface FlowRunRecord {
  flowRunId: string;
  flowId: string;
  flowVersion: string;
  status: 'queued' | 'running' | 'completed' | 'failed' | 'canceled';
  inputs?: Record<string, any>;
  output?: Record<string, any>;
  steps: Array<{
    stepId: string;
    stepIndex: number;
    status: 'pending' | 'running' | 'completed' | 'failed' | 'skipped';
    startedAt?: string;
    endedAt?: string;
    resolvedInputs?: Record<string, any>;
    output?: any;
    error?: { code?: string; message?: string; details?: any };
    interactionId?: string;
    cost?: { totalCost: number; currency: string };
    durationMs?: number;
  }>;
  totalCost?: { totalCost: number; currency: string };
  totalDurationMs?: number;
  cancelable?: boolean;
  error?: { code?: string; message?: string };
  createdAt: string;
  updatedAt: string;
}

export interface ScenarioModelRef {
  providerType: string;
  modelId: string;
  accountId?: string;
}

export interface ScenarioAsset {
  fieldName: string;
  label?: string;
  originalName: string;
  mimeType: string;
  size: number;
  storageBucket?: string;
  storageKey?: string;
  storageUri?: string;
  storageUrl?: string;
}

export interface Scenario {
  id: string;
  scenarioId: string;
  name: string;
  description?: string;
  tags?: string[];
  studioState: {
    selectedModels: ScenarioModelRef[];
    prompt: string;
    promptParameters?: Record<string, any>;
    modelParameters?: Record<string, any>;
    outputTemplate?: string;
    inputAssets?: ScenarioAsset[];
    modelSelectionState?: {
      search?: string;
      filters?: {
        text?: boolean;
        image?: boolean;
        video?: boolean;
        audio?: boolean;
        music?: boolean;
        supportsReferenceInput?: boolean;
      };
      sort?: 'most-used' | 'name' | 'provider';
    };
  };
  latestExecutionGroupId?: string;
  sourcePromptId?: string;
  sourcePromptVersion?: string;
  createdAt: string;
  updatedAt: string;
}

export interface ExecutionJob {
  jobId: string;
  executionGroupId: string;
  scenarioId?: string;
  interactionId?: string;
  providerType: string;
  modelId: string;
  accountId?: string;
  kind: 'text' | 'media';
  status: string;
  providerJobId?: string;
  providerRequestId?: string;
  outputText?: string;
  outputJson?: any;
  outputs?: MediaOutputAsset[];
  eval?: {
    passed?: boolean;
    score?: number;
    results?: Array<{
      rule?: string;
      passed?: boolean;
      message?: string;
      details?: any;
    }>;
  };
  cost?: { totalCost: number; currency: string; promptCost?: number; completionCost?: number; details?: any };
  timing?: { startedAt?: string; endedAt?: string; latencyMs?: number };
  error?: string;
  cancelable?: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface ExecutionBatch {
  executionGroupId: string;
  scenarioId?: string;
  promptId?: string;
  promptVersion?: string;
  status: string;
  summary: {
    total: number;
    queued: number;
    running: number;
    completed: number;
    failed: number;
    canceled: number;
  };
  jobs: ExecutionJob[];
  createdAt: string;
  updatedAt: string;
}

export interface PromptRelease {
  id: string;
  promptId: string;
  channel: string;
  version: string;
  updatedAt: string;
}

export interface InteractionListItem {
  id: string;
  interactionId: string;
  promptId?: string;
  promptName?: string;
  promptVersion?: string;
  status: string;
  AImodel?: string;
  date?: string;
  durationMs?: number;
  prompt?: string;
  response?: string;
  costValue?: number;
  modelId: string;
  providerType: string;
  providerRequestId?: string;
  cost?: { totalCost: number; currency: string; promptCost?: number; completionCost?: number; details?: any };
  tokenUsage?: { promptTokens: number; completionTokens: number; totalTokens: number };
  timing?: { startedAt: string; endedAt: string; latencyMs: number };
  executionContext?: {
    operation?: string;
    operationId?: string;
    runId?: string;
    agentId?: string;
    [key: string]: any;
  };
  isFallback?: boolean;
  fallbackInfo?: {
    attemptedModelCodes?: string[];
    defaultModelCode?: string;
    effectiveModelCode?: string;
    selectedModelCode?: string;
  };
  createdAt: string;
}

export interface InteractionDetail extends InteractionListItem {
  inputs?: Record<string, any>;
  inputFiles?: Array<{
    fieldName?: string;
    originalName?: string;
    mimeType?: string;
    size?: number;
    storageBucket?: string;
    storageKey?: string;
    storageUri?: string;
    storageUrl?: string;
  }>;
  outputText?: string;
  outputJson?: any;
  generatedMedia?: string[];
  queueResponses?: any[];
  rawResponse?: any;
  providerMetadata?: Record<string, any>;
  evalResults?: any;
  error?: string;
}

export interface AlertLogListItem {
  id: string;
  alertId: string;
  createdAt: string;
  severity: string;
  type: string;
  message: string;
  promptId?: string;
  promptName?: string;
  promptVersion?: string;
  interactionId?: string;
  defaultModelCode?: string;
  fallbackModelCode?: string;
  modelCode?: string;
  metadata?: Record<string, any>;
}

export interface CostAnalytics {
  totalCost: number;
  totalCount: number;
  avgCost: number;
  costByPrompt: Array<{ promptId: string; totalCost: number; count: number }>;
  costByModel: Array<{ model: string; totalCost: number; count: number }>;
  costOverTime: Array<{ date: string; totalCost: number; count: number }>;
}

export interface InteractionAnalyticsResponse {
  data: {
    metrics: {
      call_volume: number;
      billable_calls: number;
      total_cost: number;
      avg_latency_ms: number;
      avg_cost_per_call: number;
      rows_used: number;
      data_freshness_utc: string;
    };
    breakdowns: {
      by_provider_model: Array<{
        providerType: string;
        modelId: string;
        call_volume: number;
        billable_calls: number;
        total_cost: number;
        avg_latency_ms: number;
        avg_cost_per_call: number;
      }>;
      by_prompt: Array<{
        promptId: string;
        call_volume: number;
        total_cost: number;
      }>;
      by_time: Array<{
        bucket_start_utc: string;
        call_volume: number;
        billable_calls: number;
        total_cost: number;
        avg_latency_ms: number;
      }>;
    };
  };
  total: number;
  meta: {
    timezone: 'UTC';
    bucket: 'hour' | 'day';
    generated_at_utc: string;
  };
}

export type AssetRetentionTier = 'ephemeral' | 'permanent';

export interface Project {
  id: string;
  projectId: string;
  name: string;
  description?: string;
  status?: 'active' | 'archived';
  metadata?: Record<string, any>;
  ownerUserId?: string;
  /**
   * Retention tier for generated assets (not inputs). 'ephemeral' = URLs are
   * re-presigned on every read and objects are purged after 14 days.
   * 'permanent' = URLs never expire for the life of the project. See
   * verna-promptlab/docs/ROADMAP-asset-retention.md.
   */
  assetRetention?: AssetRetentionTier;
  /** Distribution list for operational alert emails; empty → project owner. */
  alertRecipients?: string[];
  currentUserRole?: 'owner' | 'admin' | 'user';
  canManageAccess?: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface ProjectMember {
  id: string;
  projectId: string;
  userId: string;
  role: 'owner' | 'admin' | 'user';
  status: 'active' | 'revoked';
  acceptedAt?: string;
  revokedAt?: string;
  createdAt: string;
  updatedAt: string;
  user?: {
    id: string;
    username: string;
    email: string;
    fullName: string;
    isActive: boolean;
  };
}

export interface ProjectInvitation {
  id: string;
  projectId: string;
  projectName?: string;
  email: string;
  role: 'admin' | 'user';
  status: 'pending' | 'accepted' | 'declined' | 'revoked' | 'expired';
  invitedUserId?: string;
  expiresAt?: string;
  acceptedAt?: string;
  declinedAt?: string;
  revokedAt?: string;
  createdAt?: string;
  updatedAt?: string;
  requiresLogin?: boolean;
  canSelfRegister?: boolean;
}

export interface ProjectAccessSnapshot {
  members: ProjectMember[];
  invitations: ProjectInvitation[];
}

export interface ProjectApiKey {
  id: string;
  projectId: string;
  keyId: string;
  name: string;
  description?: string;
  tokenPreview: string;
  scopes: string[];
  createdByUserId: string;
  lastUsedAt?: string;
  expiresAt?: string;
  revokedAt?: string;
  createdAt: string;
  updatedAt: string;
  status: 'active' | 'expired' | 'revoked';
}

export interface ProviderAccount {
  id: string;
  accountId: string;
  providerType: string;
  name: string;
  description?: string;
  active: boolean;
  lastTestResult?: {
    success: boolean;
    error?: string;
    testedAt: string;
  };
  createdAt: string;
  updatedAt: string;
}

export interface RuntimeModel {
  id?: string;
  key: string;
  providerType: string;
  modelId: string;
  providerModelId?: string;
  providerOperation?: string;
  name: string;
  description?: string;
  family?: string;
  modality?: 'text' | 'image' | 'video' | 'audio' | 'music' | 'mixed';
  executionKind?: 'text' | 'media';
  executionMode?: 'sync' | 'async';
  normalizedId?: string;
  contextWindow?: number;
  capabilities?: {
    supportsTextGeneration?: boolean;
    supportsImageGeneration?: boolean;
    supportsVideoGeneration?: boolean;
    supportsAudioGeneration?: boolean;
    supportsMusicGeneration?: boolean;
    supportsFileUpload?: boolean;
    supportsImageInput?: boolean;
    supportsMultipleImages?: boolean;
    supportsStartImage?: boolean;
    supportsEndImage?: boolean;
    supportsMusicReference?: boolean;
    functionCalling?: boolean;
    streaming?: boolean;
    vision?: boolean;
    audio?: boolean;
    contextWindow?: number;
    outputFormats?: string[];
    [key: string]: any;
  };
  acceptedInputs?: {
    file?: boolean;
    maxFileCount?: number;
    image?: boolean;
    multipleImages?: boolean;
    maxImageCount?: number;
    audio?: boolean;
    startImage?: boolean;
    endImage?: boolean;
  };
  inputSchema?: {
    fields?: Array<Record<string, any>>;
    raw?: any;
  };
  pricing?: {
    billingType?: 'tokens' | 'unit' | 'hybrid';
    promptTokenCost?: number;
    completionTokenCost?: number;
    unitPrice?: number;
    unit?: string;
    currency?: string;
    rules?: Array<Record<string, any>>;
    summaryText?: string;
  };
  docs?: {
    providerDocsUrl?: string;
    providerPlaygroundUrl?: string;
    sdkDocsUrl?: string;
  };
  status?: string;
  tags?: string[];
  metadata?: Record<string, any>;
  accountIds?: string[];
}

export interface ModelRegistryRecord extends RuntimeModel {
  id?: string;
  source?: Record<string, any>;
  overrides?: Record<string, any>;
  lastSeenAt?: string;
  createdAt?: string;
  updatedAt?: string;
}

// ─── Project Model Library ────────────────────────────────────────────────────

/**
 * Per-second pricing tiers for video / audio models. Used when
 * `billingType === 'per-second'`. Each value is the price per generated
 * second at that resolution. The router maps actual output dimensions to
 * the largest-side tier (≤480p → sd, ≤720p → hd, ≤1080p → fullhd, ≤1440p
 * → qhd, ≤2160p → uhd, > 2160p → eightk). `default` is the fallback for
 * audio/music or when the resolution can't be determined.
 */
export interface ProjectModelPerSecondPricing {
  sd?: number;
  hd?: number;
  fullhd?: number;
  qhd?: number;
  uhd?: number;
  eightk?: number;
  default?: number;
  /**
   * Multiplier applied to the resolved tier price when the call has
   * `generate_audio: false`. Default 1.0 (no effect). Veo 3.1: 0.67.
   */
  noAudioPriceFactor?: number;
}

export interface ProjectModelPricing {
  /**
   * Billing model:
   *   - `tokens`           — LLMs: $/1M prompt+completion tokens
   *   - `per-image`        — flat $ per generated image
   *   - `per-megapixel`    — output megapixels × unitPrice (Flux Pro family)
   *   - `per-second`       — generated seconds × tier price (video/audio)
   *   - `per-character`    — char chunks × unitPrice (TTS)
   *   - `per-compute-second` — provider compute seconds × unitPrice (Replicate)
   *   - `per-execution`    — flat $ per call/request (tool providers like
   *                          Scrappey scrape, Tavily search-per-credit,
   *                          single-shot APIs). The router translates this
   *                          to the lib's `unit` evaluator which defaults
   *                          unitCount=1 per call.
   *   - `free`             — never bills
   *   - `other`            — opaque (credits, custom). UI shows `summaryText`.
   */
  billingType:
    | 'tokens'
    | 'per-image'
    | 'per-second'
    | 'per-megapixel'
    | 'per-character'
    | 'per-compute-second'
    | 'per-execution'
    | 'free'
    | 'other';
  inputCostPer1M?: number;
  outputCostPer1M?: number;
  /**
   * Generic price-per-billed-unit. Used by every non-token billing type:
   *   - per-image           → $ per generated image
   *   - per-megapixel       → $ per output megapixel (W×H ÷ 1M)
   *   - per-character       → $ per `characterChunkSize` characters
   *   - per-compute-second  → $ per provider compute second
   *   - other               → $ per opaque unit
   */
  unitPrice?: number;
  /**
   * Chunk size for `per-character` billing — how many characters
   * `unitPrice` covers. Defaults to 1000 when omitted.
   */
  characterChunkSize?: number;
  perSecondPricing?: ProjectModelPerSecondPricing;
  pricingNote?: string;
  summaryText?: string;
}

export interface ProjectModelCapabilities {
  contextWindow?: number;
  functionCalling?: boolean;
  streaming?: boolean;
  vision?: boolean;
}

export interface ProjectModelAcceptedInputs {
  text?: boolean;
  image?: boolean;
  audio?: boolean;
  file?: boolean;
  referenceImage?: boolean;
  multipleImages?: boolean;
}

export interface ProjectModel {
  id: string;
  providerType: string;
  providerModelId: string;
  modelCode: string;
  displayName: string;
  modality: 'text' | 'image' | 'video' | 'audio' | 'music';
  executionKind: 'text' | 'media';
  executionMode: 'sync' | 'async';
  acceptedInputs?: ProjectModelAcceptedInputs;
  capabilities?: ProjectModelCapabilities;
  pricing?: ProjectModelPricing;
  description?: string;
  tags?: string[];
  notes?: string;
  docsUrl?: string;
  status: 'active' | 'deprecated';
  addedBy: string;
  createdAt: string;
  updatedAt: string;
  health?: HealthResult;
}

export interface LiveModelOption {
  providerType: string;
  providerModelId: string;
  displayName: string;
  description?: string;
  modality: 'text' | 'image' | 'video' | 'audio' | 'music';
  executionKind: 'text' | 'media';
  executionMode: 'sync' | 'async';
  acceptedInputs?: ProjectModelAcceptedInputs;
  capabilities?: ProjectModelCapabilities;
  pricing?: ProjectModelPricing;
  providerSnapshot?: Record<string, any>;
}

export interface CreateProjectModelPayload {
  providerType: string;
  providerModelId: string;
  displayName: string;
  modality: 'text' | 'image' | 'video' | 'audio' | 'music';
  executionKind: 'text' | 'media';
  executionMode?: 'sync' | 'async';
  acceptedInputs?: ProjectModelAcceptedInputs;
  capabilities?: ProjectModelCapabilities;
  pricing?: ProjectModelPricing;
  description?: string;
  tags?: string[];
  notes?: string;
  docsUrl?: string;
  providerSnapshot?: Record<string, any>;
}

export interface ModelFavoriteEntry {
  id: string;
  providerType: string;
  modelId: string;
  modelName?: string;
  scopeLevel: 'project' | 'company';
  scope?: {
    companyId?: string;
    projectId?: string;
    workspaceId?: string;
    environment?: string;
  };
  inheritedFrom?: 'project' | 'company';
  createdAt: string;
  updatedAt: string;
}

export interface ModelFavoritesSnapshot {
  projectFavorites: ModelFavoriteEntry[];
  companyFavorites: ModelFavoriteEntry[];
  effectiveFavorites: ModelFavoriteEntry[];
  favoriteKeys: string[];
}

/**
 * Provider types the registry's model-sync controller will accept on
 * `POST /model-sync/{providerType}/run`. Keep this in sync with the
 * server's allowlist at
 * `promptlab-registry-service/src/model-sync/model-sync.controller.ts`.
 * Tool providers (tavily/scrappey) live in the catalog too — they
 * publish pseudo-models the router dispatches as tool calls.
 */
export type SyncProviderType =
  | 'mureka'
  | 'fal'
  | 'openrouter'
  | 'openai'
  | 'google'
  | 'anthropic'
  | 'replicate'
  | 'perplexity'
  // Tier-1 LLM expansion (added 2026-05-05).
  | 'xai'
  | 'deepseek'
  | 'mistral'
  | 'cohere'
  // Tier-2 (added 2026-05-05).
  | 'groq'
  // Tool providers — router-only at execute time, but their pseudo-models
  // need to be in the catalog so projects can favorite them and prompts
  // can target `tavily::search` / `scrappey::scrape` etc.
  | 'tavily'
  | 'scrappey';

export interface ModelSyncStatus {
  providerType: SyncProviderType;
  scope?: {
    projectId?: string;
    workspaceId?: string;
    environment?: string;
  };
  startedAt?: string;
  finishedAt?: string;
  processed?: number;
  created?: number;
  updated?: number;
  deprecated?: number;
  total?: number;
  currentModelId?: string;
  lastMessage?: string;
  errors?: Array<{ modelKey?: string; message: string }>;
  status: 'idle' | 'running' | 'completed' | 'failed';
}

export interface MediaOutputAsset {
  type: 'image' | 'video' | 'audio' | 'file' | 'text' | 'json';
  url?: string;
  mimeType?: string;
  text?: string;
  data?: any;
  storageBucket?: string;
  storageKey?: string;
  storageUri?: string;
}

export interface MediaExecutionRequest {
  providerType: string;
  model: string;
  prompt?: string;
  promptId?: string;
  promptVersion?: string;
  promptName?: string;
  accountId?: string;
  input?: Record<string, any>;
  options?: Record<string, any>;
  context?: Record<string, any>;
  trace?: Record<string, any>;
  files?: Array<{
    fieldName: string;
    file: File;
  }>;
}

export interface MediaExecutionAccepted {
  jobId: string;
  providerType: string;
  providerJobId: string;
  model: string;
  status: string;
  cost?: { totalCost: number; currency: string; promptCost?: number; completionCost?: number; details?: any };
  streamUrl?: string;
  resultUrl?: string;
  cancelUrl?: string;
  pollAfterMs: number;
  submittedAt: string;
}

export interface MediaExecutionStatus {
  jobId: string;
  providerType: string;
  providerJobId: string;
  model: string;
  status: string;
  cost?: { totalCost: number; currency: string; promptCost?: number; completionCost?: number; details?: any };
  streamUrl?: string;
  resultUrl?: string;
  cancelUrl?: string;
  outputs?: MediaOutputAsset[];
  error?: string;
  updatedAt: string;
}

export interface MediaExecutionResult {
  jobId: string;
  providerRequestId?: string;
  status: string;
  outputs: MediaOutputAsset[];
  cost?: { totalCost: number; currency: string; promptCost?: number; completionCost?: number; details?: any };
  resultUrl?: string;
}

const isRecord = (value: unknown): value is Record<string, any> =>
  value !== null && typeof value === 'object' && !Array.isArray(value);

const asString = (value: unknown, fallback = ''): string =>
  typeof value === 'string' ? value : fallback;

const asNumber = (value: unknown, fallback = 0): number =>
  typeof value === 'number' && Number.isFinite(value) ? value : fallback;

const asArray = <T>(value: unknown, mapper: (item: any) => T): T[] =>
  Array.isArray(value) ? value.map(mapper) : [];

const projectScopedPath = (path: string, projectId?: string | null): string => {
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  if (projectId) {
    return `/${encodeURIComponent(projectId)}${normalizedPath}`;
  }
  return normalizedPath;
};

const routerProjectScopedPath = (path: string, projectId?: string | null): string => {
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  if (projectId) {
    return `${routerApiPathPrefix}/${encodeURIComponent(projectId)}${normalizedPath}`;
  }
  return `${routerApiPathPrefix}${normalizedPath}`;
};

const buildRouterHeaders = (): Record<string, string> => {
  const headers: Record<string, string> = {};
  const projectId = getCurrentProjectId();
  if (projectId) {
    headers['x-project-id'] = projectId;
    const routerApiKey = getRouterApiKey(projectId);
    if (routerApiKey) {
      headers['x-api-key'] = routerApiKey;
    }
  }
  return headers;
};

const parseInteractionAnalyticsResponse = (response: any): InteractionAnalyticsResponse => {
  if (!isRecord(response)) {
    return {
      data: {
        metrics: {
          call_volume: 0,
          billable_calls: 0,
          total_cost: 0,
          avg_latency_ms: 0,
          avg_cost_per_call: 0,
          rows_used: 0,
          data_freshness_utc: new Date().toISOString(),
        },
        breakdowns: {
          by_provider_model: [],
          by_prompt: [],
          by_time: [],
        },
      },
      total: 0,
      meta: {
        timezone: 'UTC',
        bucket: 'day',
        generated_at_utc: new Date().toISOString(),
      },
    };
  }

  const data = isRecord(response.data) ? response.data : {};
  const metrics = isRecord(data.metrics) ? data.metrics : {};
  const breakdowns = isRecord(data.breakdowns) ? data.breakdowns : {};
  const rawBucket = asString((response.meta as any)?.bucket, 'day');
  const bucket: 'hour' | 'day' = rawBucket === 'hour' ? 'hour' : 'day';

  return {
    data: {
      metrics: {
        call_volume: asNumber(metrics.call_volume),
        billable_calls: asNumber(metrics.billable_calls),
        total_cost: asNumber(metrics.total_cost),
        avg_latency_ms: asNumber(metrics.avg_latency_ms),
        avg_cost_per_call: asNumber(metrics.avg_cost_per_call),
        rows_used: asNumber(metrics.rows_used),
        data_freshness_utc: asString(metrics.data_freshness_utc, new Date().toISOString()),
      },
      breakdowns: {
        by_provider_model: asArray(breakdowns.by_provider_model, (item) => ({
          providerType: asString(item?.providerType, 'unknown'),
          modelId: asString(item?.modelId, 'unknown'),
          call_volume: asNumber(item?.call_volume),
          billable_calls: asNumber(item?.billable_calls),
          total_cost: asNumber(item?.total_cost),
          avg_latency_ms: asNumber(item?.avg_latency_ms),
          avg_cost_per_call: asNumber(item?.avg_cost_per_call),
        })),
        by_prompt: asArray(breakdowns.by_prompt, (item) => ({
          promptId: asString(item?.promptId, 'unknown'),
          call_volume: asNumber(item?.call_volume),
          total_cost: asNumber(item?.total_cost),
        })),
        by_time: asArray(breakdowns.by_time, (item) => ({
          bucket_start_utc: asString(item?.bucket_start_utc, new Date().toISOString()),
          call_volume: asNumber(item?.call_volume),
          billable_calls: asNumber(item?.billable_calls),
          total_cost: asNumber(item?.total_cost),
          avg_latency_ms: asNumber(item?.avg_latency_ms),
        })),
      },
    },
    total: asNumber(response.total),
    meta: {
      timezone: 'UTC',
      bucket,
      generated_at_utc: asString(
        (response.meta as any)?.generated_at_utc,
        new Date().toISOString(),
      ),
    },
  };
};

const toIsoString = (value: any): string | undefined => {
  if (!value) return undefined;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? undefined : parsed.toISOString();
};

const toInteractionText = (value: any): string | undefined => {
  if (value == null) return undefined;
  if (typeof value === 'string') return value;
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
};

const extractPromptText = (item: any): string | undefined => {
  if (typeof item?.prompt === 'string' && item.prompt.trim()) {
    return item.prompt;
  }
  if (typeof item?.inputs?.prompt === 'string' && item.inputs.prompt.trim()) {
    return item.inputs.prompt;
  }
  const template = item?.promptSpec?.template;
  if (Array.isArray(template)) {
    const content = template
      .map((entry: any) => (typeof entry?.content === 'string' ? entry.content : ''))
      .filter(Boolean)
      .join('\n\n');
    if (content.trim()) return content;
  }
  return undefined;
};

const extractInteractionPayload = (response: any): Record<string, any> => {
  if (!isRecord(response)) return {};
  if (isRecord(response.data)) return extractInteractionPayload(response.data);
  if (isRecord(response._doc)) return extractInteractionPayload(response._doc);
  return response;
};

const normalizeRuntimeModel = (item: any): RuntimeModel => {
  const providerType = asString(item?.providerType, 'unknown');
  const modelId = asString(item?.modelId || item?.providerModelId || item?.id, 'unknown');
  const capabilities = isRecord(item?.capabilities) ? item.capabilities : undefined;
  const acceptedInputs = isRecord(item?.acceptedInputs) ? item.acceptedInputs : undefined;
  const pricing = isRecord(item?.pricing) ? item.pricing : undefined;
  const docs = isRecord(item?.docs) ? item.docs : undefined;
  const inputSchema = isRecord(item?.inputSchema) ? item.inputSchema : undefined;
  const metadata = isRecord(item?.metadata) ? item.metadata : undefined;

  return {
    id: item?.id ? String(item.id) : undefined,
    key: asString(item?.key, `${providerType}::${modelId}`),
    providerType,
    modelId,
    providerModelId: item?.providerModelId ? String(item.providerModelId) : undefined,
    providerOperation: item?.providerOperation ? String(item.providerOperation) : undefined,
    name: asString(item?.name, modelId),
    description: item?.description ? String(item.description) : undefined,
    family: item?.family ? String(item.family) : undefined,
    modality: item?.modality,
    executionKind: item?.executionKind,
    executionMode: item?.executionMode,
    normalizedId: item?.normalizedId ? String(item.normalizedId) : undefined,
    contextWindow: typeof item?.contextWindow === 'number' ? item.contextWindow : undefined,
    capabilities,
    acceptedInputs,
    inputSchema: inputSchema
      ? {
          fields: Array.isArray(inputSchema.fields) ? inputSchema.fields : undefined,
          raw: inputSchema.raw,
        }
      : undefined,
    pricing: pricing
      ? {
          billingType: pricing.billingType,
          promptTokenCost: typeof pricing.promptTokenCost === 'number' ? pricing.promptTokenCost : undefined,
          completionTokenCost: typeof pricing.completionTokenCost === 'number' ? pricing.completionTokenCost : undefined,
          unitPrice: typeof pricing.unitPrice === 'number' ? pricing.unitPrice : undefined,
          unit: pricing.unit ? String(pricing.unit) : undefined,
          currency: pricing.currency ? String(pricing.currency) : undefined,
          rules: Array.isArray(pricing.rules) ? pricing.rules : undefined,
          summaryText: pricing.summaryText ? String(pricing.summaryText) : undefined,
        }
      : undefined,
    docs: docs
      ? {
          providerDocsUrl: docs.providerDocsUrl ? String(docs.providerDocsUrl) : undefined,
          providerPlaygroundUrl: docs.providerPlaygroundUrl ? String(docs.providerPlaygroundUrl) : undefined,
          sdkDocsUrl: docs.sdkDocsUrl ? String(docs.sdkDocsUrl) : undefined,
        }
      : undefined,
    status: item?.status ? String(item.status) : undefined,
    tags: Array.isArray(item?.tags) ? item.tags.map((tag: any) => String(tag)) : undefined,
    metadata,
    accountIds: Array.isArray(item?.accountIds)
      ? item.accountIds.map((accountId: any) => String(accountId))
      : undefined,
  };
};

const normalizeRuntimeModelsResponse = (
  response: any,
): { generatedAt: string; total: number; models: RuntimeModel[] } => {
  const payload = isRecord(response?.data) ? response.data : response;
  const rows = Array.isArray(payload?.models)
    ? payload.models
    : Array.isArray(payload?.data)
      ? payload.data
      : Array.isArray(payload)
        ? payload
        : [];
  const models = rows
    .map((item) => normalizeRuntimeModel(item))
    .filter((item) => item.providerType && item.providerType !== 'unknown' && item.modelId && item.modelId !== 'unknown');

  return {
    generatedAt: asString(payload?.generatedAt || payload?.generated_at_utc, new Date().toISOString()),
    total: typeof payload?.total === 'number' ? payload.total : models.length,
    models,
  };
};

const normalizeInteraction = (item: any): InteractionListItem => {
  const providerType = item?.providerType || 'unknown';
  const modelId = item?.modelId || item?.providerModelId || item?.AImodel || 'unknown';
  const interactionId = item?.interactionId || item?.id || item?._id || '';
  const date = toIsoString(item?.date || item?.timing?.startedAt || item?.createdAt);
  const createdAt = date || new Date().toISOString();
  const durationMs =
    typeof item?.durationMs === 'number'
      ? item.durationMs
      : typeof item?.duration === 'number'
        ? item.duration
        : typeof item?.timing?.latencyMs === 'number'
          ? item.timing.latencyMs
          : undefined;
  const totalCost =
    typeof item?.cost === 'number'
      ? item.cost
      : typeof item?.cost?.totalCost === 'number'
        ? item.cost.totalCost
        : undefined;
  const prompt = extractPromptText(item);
  const response =
    toInteractionText(item?.response) ??
    toInteractionText(item?.outputText) ??
    toInteractionText(item?.outputJson?.assets) ??
    toInteractionText(item?.outputJson) ??
    toInteractionText(item?.rawResponse);
  const fallbackInfo = isRecord(item?.providerMetadata?.fallbackInfo)
    ? item.providerMetadata.fallbackInfo
    : undefined;
  const isFallback = Boolean(item?.isFallback || item?.providerMetadata?.isFallback || fallbackInfo);

  return {
    id: String(item?.id || item?._id || interactionId),
    interactionId: String(interactionId),
    promptId: item?.promptId,
    promptName: item?.promptName,
    promptVersion: item?.promptVersion,
    status: item?.status || 'unknown',
    AImodel: item?.AImodel || `${providerType}/${modelId}`,
    date: createdAt,
    durationMs,
    prompt,
    response,
    costValue: totalCost,
    modelId,
    providerType,
    providerRequestId:
      item?.providerRequestId ||
      item?.openRouterID ||
      item?.providerMetadata?.providerRequestId ||
      item?.providerMetadata?.requestId,
    cost: item?.cost
      ? {
          totalCost:
            typeof item.cost.totalCost === 'number'
              ? item.cost.totalCost
              : typeof totalCost === 'number'
                ? totalCost
                : 0,
          currency: item.cost.currency || 'USD',
          promptCost: item.cost.promptCost,
          completionCost: item.cost.completionCost,
          details: item.cost.details,
        }
      : undefined,
    tokenUsage: item?.tokenUsage || item?.usage,
    timing: item?.timing
      ? item.timing
      : date
        ? {
            startedAt: date,
            endedAt: toIsoString(item?.timing?.endedAt || item?.updatedAt || item?.createdAt) || date,
            latencyMs: typeof durationMs === 'number' ? durationMs : 0,
          }
        : undefined,
    executionContext: item?.executionContext || {
      operation: item?.operation,
      operationId: item?.operationID,
      runId: item?.runID,
      agentId: item?.agentID,
    },
    isFallback,
    fallbackInfo: fallbackInfo
      ? {
          attemptedModelCodes: Array.isArray(fallbackInfo.attemptedModelCodes)
            ? fallbackInfo.attemptedModelCodes.map((entry: any) => String(entry))
            : undefined,
          defaultModelCode:
            typeof fallbackInfo.defaultModelCode === 'string'
              ? fallbackInfo.defaultModelCode
              : undefined,
          effectiveModelCode:
            typeof fallbackInfo.effectiveModelCode === 'string'
              ? fallbackInfo.effectiveModelCode
              : undefined,
          selectedModelCode:
            typeof fallbackInfo.selectedModelCode === 'string'
              ? fallbackInfo.selectedModelCode
              : undefined,
        }
      : undefined,
    createdAt,
  };
};

const normalizeAlertLog = (item: any): AlertLogListItem => ({
  id: String(item?.id || item?._id || item?.alertId || ''),
  alertId: String(item?.alertId || item?.id || item?._id || ''),
  createdAt: toIsoString(item?.createdAt) || new Date().toISOString(),
  severity: asString(item?.severity, 'warning'),
  type: asString(item?.type, 'unknown'),
  message: asString(item?.message, ''),
  promptId: item?.promptId ? String(item.promptId) : undefined,
  promptName: item?.promptName ? String(item.promptName) : undefined,
  promptVersion: item?.promptVersion ? String(item.promptVersion) : undefined,
  interactionId: item?.interactionId ? String(item.interactionId) : undefined,
  defaultModelCode: item?.defaultModelCode ? String(item.defaultModelCode) : undefined,
  fallbackModelCode: item?.fallbackModelCode ? String(item.fallbackModelCode) : undefined,
  modelCode: item?.modelCode ? String(item.modelCode) : undefined,
  metadata: isRecord(item?.metadata) ? item.metadata : undefined,
});

// Registry API
// ─── Lab Assistant types ─────────────────────────────────────────────
// Mirrors the registry-side DTOs in
// promptlab-registry-service/src/lab-assistant/dto/*.ts

export type LabAssistantStatus = { ready: boolean; reason?: string };

export interface LabAssistantResponse {
  operation: string;
  result: Record<string, any>;
  // The underlying provider model PromptLab routes Lab Assistant calls
  // through is intentionally NOT surfaced in this type. End users
  // never see the model identity; ops can audit it via the
  // `lab-assistant` project's interactions explorer server-side.
  latencyMs?: number;
  labAssistantInteractionId?: string;

  // ─── Billing snapshot (Phase B.2) ─────────────────────────────────
  // Set when the call carried an authenticated user with a real
  // subscription. Drives `CreditBalanceWidget` and the `CostPreview`
  // tooltip — the response carries fresh values so we don't need a
  // follow-up GET /entitlements after every call. Always omitted on
  // unauthenticated / super-admin / Prompt-Studio paths (those don't
  // bill, so they don't surface balance).

  /** Credits consumed by this call (= cost USD × tier multiplier). */
  creditsBurned?: number;
  /** Balance AFTER this call. plan + purchased = total available. */
  creditsRemaining?: { plan: number; purchased: number };
  /** Caller's tier costMultiplier. UI uses this to upsell upgrades. */
  creditMultiplier?: number;
}

/**
 * Query payload for the cost-estimate endpoint. Optional `model`
 * narrows the estimate to a specific provider/model.
 */
export interface CostEstimateRequest {
  operation: LabAssistantOperationType;
  model?: string;
}

/**
 * Cost-preview range surfaced under every Lab Assistant action
 * button. `creditsLow` and `creditsHigh` are already multiplied by
 * the user's tier `costMultiplier` — render directly. `basis:
 * "fallback"` means the historical sample size was below the
 * confidence threshold and the range comes from a hard-coded table
 * — UI should append a small "estimated" hint.
 */
export interface CostEstimateResponse {
  operation: string;
  model?: string;
  creditsLow: number;
  creditsHigh: number;
  costMultiplier: number;
  sampleSize: number;
  basis: 'historical' | 'fallback';
}

export interface MetapromptExamplePair {
  input: string;
  output: string;
}

export interface MetapromptConstraints {
  maxTokens?: number;
  tone?: string;
  outputFormat?: 'text' | 'json' | 'markdown' | 'list' | 'custom';
  outputSchema?: Record<string, any>;
}

export interface MetapromptRequest {
  taskDescription: string;
  constraints?: MetapromptConstraints;
  examplePairs?: MetapromptExamplePair[];
  targetModels: string[];
}

export interface CoachRequest {
  currentPrompt: string;
  currentModel?: string;
  evalFailure?: Record<string, any>;
  sampleOutput?: string;
}

export interface OptimizeRequest {
  currentPrompt: string;
  fromModel?: string;
  toModel: string;
  baselineInteractionIds?: string[];
  optimizeFor?: Array<'accuracy' | 'cost' | 'latency' | 'brevity' | 'faithfulness'>;
  evalRules?: string[];
}

export interface OptimizeForCohortRequest {
  currentPrompt: string;
  cohortModels: string[];
  fromModel?: string;
  optimizeFor?: Array<'accuracy' | 'cost' | 'latency' | 'brevity' | 'faithfulness'>;
  evalRules?: string[];
}

export interface RefineRequest {
  currentPrompt: string;
  refinementNote: string;
  currentModel?: string;
}

// Per-model fairness note returned by optimize-for-cohort. Renders
// alongside the unified prompt diff so the user sees what each model is
// expected to do with the prompt.
export interface CohortModelNote {
  modelCode: string;
  note: string;
}

/** Lab Assistant operation type — matches the backend enum. */
export type LabAssistantOperationType =
  | 'metaprompt'
  | 'coach'
  | 'optimize'
  | 'optimize-for-cohort'
  | 'refine'
  | 'translate-syntax'
  | 'vary'
  | 'explain'
  | 'auto-name'
  | 'auto-tag'
  | 'judge';

/** One entry in a scenario's `labAssistantHistory[]`. */
export interface LabAssistantHistoryEntry {
  eventId: string;
  operation: LabAssistantOperationType;
  timestamp: string;
  request: Record<string, any>;
  result: Record<string, any>;
  outcome: 'applied' | 'discarded';
  previousPrompt?: string;
  appliedPrompt?: string;
  labAssistantInteractionId?: string;
  notes?: string;
}

/** Body for the append endpoint. */
export interface AppendLabAssistantEventRequest {
  scenarioId: string;
  operation: LabAssistantOperationType;
  request: Record<string, any>;
  result: Record<string, any>;
  outcome: 'applied' | 'discarded';
  previousPrompt?: string;
  appliedPrompt?: string;
  labAssistantInteractionId?: string;
  notes?: string;
}

export interface TranslateSyntaxRequest {
  currentPrompt: string;
  fromProvider: string;
  toProvider: string;
  cohortProviders?: string[];
}

/** One variation axis the user wants to produce. `name` is the variant label, `instruction` is the rewrite direction. */
export interface VariationSpec {
  name: string;
  instruction: string;
}

/** One variant returned by `vary` — name echoes the input axis name. */
export interface VariationVariant {
  name: string;
  prompt: string;
  rationale?: string;
}

export interface VaryRequest {
  currentPrompt: string;
  /** 1-5 variation axes. Server enforces the upper bound. */
  variations: VariationSpec[];
  /** Optional model the base prompt currently targets — preserves syntax conventions in variants. */
  currentModel?: string;
}

export interface AutoNameRequest {
  content: string;
  contentType: 'prompt' | 'scenario';
  existingNames?: string[];
}

export interface AutoTagRequest {
  content: string;
  existingTags?: string[];
}

// Surface key sent with each rating — lets the dashboard query
// "up-rate of the Optimize sheet vs the Coach panel" without having
// to reverse-engineer it from the operation.
export type LabAssistantSurfaceKey =
  | 'compose'
  | 'optimize'
  | 'optimize-for-cohort'
  | 'refine'
  | 'coach'
  | 'translate'
  | 'variations'
  | 'explain'
  | 'auto-name'
  | 'auto-tag';

export interface RateLabAssistantSuggestionRequest {
  labAssistantInteractionId: string;
  rating: 'up' | 'down';
  note?: string;
  surface?: LabAssistantSurfaceKey;
  outcome?: 'applied' | 'discarded' | 'ignored';
}

export interface RateLabAssistantSuggestionResponse {
  success: boolean;
  labAssistantInteractionId: string;
}

export interface ExplainRequest {
  interactionId: string;
}

// Per-model variant returned by the metaprompt endpoint. Lab Assistant's
// `metaprompt` stored prompt is expected to emit this shape under
// `result.perModelPrompts[]`.
export interface MetapromptVariant {
  modelCode: string;
  prompt: string;
  rationale?: string;
}

// Coach returns a ranked list of suggestion cards. Each suggestion has
// a one-line title, a longer rationale, and the proposed edit.
export interface CoachSuggestion {
  title: string;
  rationale: string;
  suggestedEdit: string;
}

export const registryApi = createApi({
  reducerPath: 'registryApi',
  baseQuery: baseQueryRegistryWithReauth,
  tagTypes: [
    'Projects',
    'ProjectApiKeys',
    'Prompts',
    'Versions',
    'Releases',
    'Interactions',
    'AlertLogs',
    'ProviderAccounts',
    'RuntimeModels',
    'ModelFavorites',
    'ProjectModels',
    'Flows',
    'FlowVersions',
    'FlowRuns',
  ],
  endpoints: (builder) => ({
    // Projects
    listProjects: builder.query<Project[], { q?: string; status?: string } | void>({
      query: (params) => ({
        url: '/projects',
        method: 'GET',
        params,
      }),
      transformResponse: (response: any) => {
        const rows = Array.isArray(response?.data)
          ? response.data
          : Array.isArray(response)
            ? response
            : [];
        return rows.filter(isRecord) as Project[];
      },
      providesTags: ['Projects'],
    }),

    createProject: builder.mutation<
      Project,
      { projectId: string; name: string; description?: string; status?: 'active' | 'archived' }
    >({
      query: (body) => ({
        url: '/projects',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Projects'],
    }),

    updateProject: builder.mutation<
      Project,
      {
        projectId: string;
        name?: string;
        description?: string;
        status?: 'active' | 'archived';
        assetRetention?: AssetRetentionTier;
        alertRecipients?: string[];
      }
    >({
      query: ({ projectId, ...body }) => ({
        url: `/projects/${projectId}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['Projects'],
    }),

    deleteProject: builder.mutation<{ success: boolean }, { projectId: string; cascade?: boolean }>({
      query: ({ projectId, cascade = true }) => ({
        url: `/projects/${projectId}`,
        method: 'DELETE',
        params: { cascade },
      }),
      invalidatesTags: ['Projects', 'Prompts', 'Versions', 'Releases', 'Interactions'],
    }),

    getProjectAccess: builder.query<ProjectAccessSnapshot, { projectId: string }>({
      query: ({ projectId }) => ({
        url: `/projects/${projectId}/access`,
        method: 'GET',
      }),
      providesTags: ['Projects'],
    }),

    listProjectApiKeys: builder.query<ProjectApiKey[], { projectId: string }>({
      query: ({ projectId }) => ({
        url: `/projects/${projectId}/api-keys`,
        method: 'GET',
      }),
      transformResponse: (response: any) =>
        (Array.isArray(response) ? response : []).filter(isRecord) as ProjectApiKey[],
      providesTags: ['ProjectApiKeys'],
    }),

    createProjectApiKey: builder.mutation<
      { apiKey: ProjectApiKey; token: string },
      {
        projectId: string;
        name: string;
        description?: string;
        expiresAt?: string;
        scopes?: string[];
      }
    >({
      query: ({ projectId, ...body }) => ({
        url: `/projects/${projectId}/api-keys`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['ProjectApiKeys'],
    }),

    revokeProjectApiKey: builder.mutation<{ success: boolean }, { projectId: string; keyId: string }>({
      query: ({ projectId, keyId }) => ({
        url: `/projects/${projectId}/api-keys/${encodeURIComponent(keyId)}/revoke`,
        method: 'POST',
      }),
      invalidatesTags: ['ProjectApiKeys'],
    }),

    inviteProjectUser: builder.mutation<
      { invitation: ProjectInvitation; inviteUrl?: string; token?: string },
      { projectId: string; email: string; fullName?: string; role: 'admin' | 'user' }
    >({
      query: ({ projectId, ...body }) => ({
        url: `/projects/${projectId}/invitations`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Projects'],
    }),

    updateProjectMemberRole: builder.mutation<
      ProjectMember,
      { projectId: string; userId: string; role: 'admin' | 'user' }
    >({
      query: ({ projectId, userId, ...body }) => ({
        url: `/projects/${projectId}/members/${userId}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['Projects'],
    }),

    revokeProjectMember: builder.mutation<{ success: boolean }, { projectId: string; userId: string }>({
      query: ({ projectId, userId }) => ({
        url: `/projects/${projectId}/members/${userId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Projects'],
    }),

    revokeProjectInvitation: builder.mutation<{ success: boolean }, { projectId: string; invitationId: string }>({
      query: ({ projectId, invitationId }) => ({
        url: `/projects/${projectId}/invitations/${invitationId}/revoke`,
        method: 'POST',
      }),
      invalidatesTags: ['Projects'],
    }),

    listMyProjectInvitations: builder.query<ProjectInvitation[], void>({
      query: () => ({
        url: '/auth/invitations',
        method: 'GET',
      }),
      transformResponse: (response: any) =>
        (Array.isArray(response) ? response : []).filter(isRecord) as ProjectInvitation[],
      providesTags: ['Projects'],
    }),

    acceptMyProjectInvitation: builder.mutation<{ success: boolean }, { invitationId: string }>({
      query: ({ invitationId }) => ({
        url: `/auth/invitations/${invitationId}/accept`,
        method: 'POST',
      }),
      invalidatesTags: ['Projects'],
    }),

    declineMyProjectInvitation: builder.mutation<{ success: boolean }, { invitationId: string }>({
      query: ({ invitationId }) => ({
        url: `/auth/invitations/${invitationId}/decline`,
        method: 'POST',
      }),
      invalidatesTags: ['Projects'],
    }),

    listProviderAccounts: builder.query<
      ProviderAccount[],
      { providerType?: string; active?: boolean; q?: string } | void
    >({
      query: (params) => ({
        url: projectScopedPath('/provider-accounts', getCurrentProjectId()),
        method: 'GET',
        params,
      }),
      transformResponse: (response: any) =>
        (Array.isArray(response?.data)
          ? response.data
          : Array.isArray(response)
            ? response
            : []
      ).filter(isRecord) as ProviderAccount[],
      providesTags: ['ProviderAccounts'],
    }),

    getModelFavorites: builder.query<ModelFavoritesSnapshot, void>({
      query: () => ({
        url: projectScopedPath('/model-favorites', getCurrentProjectId()),
        method: 'GET',
      }),
      transformResponse: (response: any) => ({
        projectFavorites: asArray(response?.projectFavorites, (item) => item as ModelFavoriteEntry),
        companyFavorites: asArray(response?.companyFavorites, (item) => item as ModelFavoriteEntry),
        effectiveFavorites: asArray(response?.effectiveFavorites, (item) => item as ModelFavoriteEntry),
        favoriteKeys: asArray(response?.favoriteKeys, (item) => asString(item)).filter(Boolean),
      }),
      providesTags: ['ModelFavorites'],
    }),

    listModelRegistryRecords: builder.query<
      { data: ModelRegistryRecord[]; total: number },
      { providerType?: string; status?: string; q?: string } | void
    >({
      query: (params) => ({
        url: '/models',
        method: 'GET',
        params,
      }),
      transformResponse: (response: any) => {
        const rows = Array.isArray(response?.data)
          ? response.data
          : Array.isArray(response)
            ? response
            : [];
        return {
          data: rows.filter(isRecord) as ModelRegistryRecord[],
          total: typeof response?.total === 'number' ? response.total : rows.length,
        };
      },
      providesTags: ['RuntimeModels'],
    }),

    // Lean, active-only (deprecated/disabled/hidden excluded), provider-filtered
    // runtime catalog — what the model picker should read. Backed by the synced
    // DB catalog via GET /models/runtime; far lighter than /models (no `source`
    // blob) so large provider catalogs (e.g. fal, ~1k models) load instantly.
    listRegistryRuntimeModels: builder.query<
      { data: ModelRegistryRecord[]; total: number },
      { providerType?: string; search?: string; limit?: number } | void
    >({
      query: (params) => ({
        url: '/models/runtime',
        method: 'GET',
        params: params || {},
      }),
      transformResponse: (response: any) => {
        const rows = Array.isArray(response?.data) ? response.data : [];
        return {
          data: rows.filter(isRecord) as ModelRegistryRecord[],
          total: typeof response?.total === 'number' ? response.total : rows.length,
        };
      },
      providesTags: ['RuntimeModels'],
    }),

    createModelRegistryRecord: builder.mutation<ModelRegistryRecord, Record<string, any>>({
      query: (body) => ({
        url: '/models',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['RuntimeModels'],
    }),

    updateModelRegistryRecord: builder.mutation<
      ModelRegistryRecord,
      { id: string; updates: Record<string, any> }
    >({
      query: ({ id, updates }) => ({
        url: `/models/by-id/${encodeURIComponent(id)}`,
        method: 'PATCH',
        body: updates,
      }),
      invalidatesTags: ['RuntimeModels'],
    }),

    upsertModelFavorite: builder.mutation<
      ModelFavoriteEntry,
      { providerType: string; modelId: string; modelName?: string; scopeLevel: 'project' | 'company' }
    >({
      query: (body) => ({
        url: projectScopedPath('/model-favorites', getCurrentProjectId()),
        method: 'POST',
        body,
      }),
      invalidatesTags: ['ModelFavorites'],
    }),

    deleteModelFavorite: builder.mutation<
      { success: boolean },
      { providerType: string; modelId: string; scopeLevel: 'project' | 'company' }
    >({
      query: ({ providerType, modelId, scopeLevel }) => ({
        url: projectScopedPath(
          `/model-favorites/${encodeURIComponent(scopeLevel)}/${encodeURIComponent(providerType)}/${encodeURIComponent(modelId)}`,
          getCurrentProjectId(),
        ),
        method: 'DELETE',
      }),
      invalidatesTags: ['ModelFavorites'],
    }),

    getModelSyncStatus: builder.query<ModelSyncStatus, { providerType: SyncProviderType }>({
      query: ({ providerType }) => ({
        url: `/model-sync/${providerType}/status`,
        method: 'GET',
      }),
    }),

    runModelSync: builder.mutation<ModelSyncStatus, { providerType: SyncProviderType }>({
      query: ({ providerType }) => ({
        url: `/model-sync/${providerType}/run`,
        method: 'POST',
      }),
      invalidatesTags: ['RuntimeModels'],
    }),

    runAllModelSync: builder.mutation<ModelSyncStatus[], void>({
      query: () => ({
        url: '/model-sync/run-all',
        method: 'POST',
      }),
      invalidatesTags: ['RuntimeModels'],
    }),

    createProviderAccount: builder.mutation<
      ProviderAccount,
      {
        accountId: string;
        providerType: string;
        name: string;
        description?: string;
        credentials: Record<string, any>;
        active?: boolean;
      }
    >({
      query: (body) => ({
        url: projectScopedPath('/provider-accounts', getCurrentProjectId()),
        method: 'POST',
        body,
      }),
      invalidatesTags: ['ProviderAccounts', 'RuntimeModels'],
    }),

    updateProviderAccount: builder.mutation<
      ProviderAccount,
      {
        accountId: string;
        name?: string;
        description?: string;
        credentials?: Record<string, any>;
        active?: boolean;
      }
    >({
      query: ({ accountId, ...body }) => ({
        url: projectScopedPath(`/provider-accounts/${accountId}`, getCurrentProjectId()),
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['ProviderAccounts', 'RuntimeModels'],
    }),

    deleteProviderAccount: builder.mutation<{ success: boolean }, { accountId: string }>({
      query: ({ accountId }) => ({
        url: projectScopedPath(`/provider-accounts/${accountId}`, getCurrentProjectId()),
        method: 'DELETE',
      }),
      invalidatesTags: ['ProviderAccounts', 'RuntimeModels'],
    }),

    listScenarios: builder.query<
      { data: Scenario[]; total: number },
      {
        q?: string;
        providerType?: string;
        modelId?: string;
        tag?: string;
        sourcePromptId?: string;
        createdBy?: string;
        limit?: number;
        offset?: number;
        orderBy?: string;
        orderDir?: 'asc' | 'desc';
      } | void
    >({
      query: (params) => ({
        url: projectScopedPath('/scenarios', getCurrentProjectId()),
        method: 'GET',
        params,
      }),
      transformResponse: (response: any) => {
        const rows = Array.isArray(response?.data)
          ? response.data
          : Array.isArray(response)
            ? response
            : [];
        return {
          data: rows.filter(isRecord) as Scenario[],
          total: typeof response?.total === 'number' ? response.total : rows.length,
        };
      },
      providesTags: ['Prompts'],
    }),

    getScenario: builder.query<Scenario, string>({
      query: (scenarioId) => ({
        url: projectScopedPath(`/scenarios/${scenarioId}`, getCurrentProjectId()),
        method: 'GET',
      }),
      providesTags: ['Prompts'],
    }),

    createScenario: builder.mutation<
      Scenario,
      {
        name: string;
        description?: string;
        tags?: string[];
        studioState: Scenario['studioState'];
        sourcePromptId?: string;
        sourcePromptVersion?: string;
        createdBy?: string;
      }
    >({
      query: (body) => ({
        url: projectScopedPath('/scenarios', getCurrentProjectId()),
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Prompts'],
    }),

    updateScenario: builder.mutation<
      Scenario,
      {
        scenarioId: string;
        name?: string;
        description?: string;
        tags?: string[];
        studioState?: Scenario['studioState'];
        latestExecutionGroupId?: string;
        sourcePromptId?: string;
        sourcePromptVersion?: string;
        updatedBy?: string;
      }
    >({
      query: ({ scenarioId, ...body }) => ({
        url: projectScopedPath(`/scenarios/${scenarioId}`, getCurrentProjectId()),
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['Prompts'],
    }),

    // Append a Lab Assistant event to a scenario's history. Fire-and-
    // forget pattern from the apply/discard handlers of the five Lab
    // Assistant surfaces — failure is silent (per ROADMAP guardrails:
    // assistant ops degrade gracefully, never block the user's flow).
    appendLabAssistantEvent: builder.mutation<
      { event: LabAssistantHistoryEntry; scenario: Scenario },
      AppendLabAssistantEventRequest
    >({
      query: ({ scenarioId, ...body }) => ({
        url: projectScopedPath(
          `/scenarios/${scenarioId}/lab-assistant-events`,
          getCurrentProjectId(),
        ),
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Prompts'],
    }),

    // Prompts
    listPrompts: builder.query<
      { data: PromptListItem[]; total: number; hasMore?: boolean; page?: number; limit?: number },
      { q?: string; intent?: string; tag?: string; model?: string; page?: number; limit?: number }
    >({
      query: (params) => ({
        url: projectScopedPath('/prompts', getCurrentProjectId()),
        method: 'GET',
        params,
      }),
      transformResponse: (response: any) => {
        if (Array.isArray(response?.data)) {
          const rows = response.data.filter(isRecord);
          const metaHasMore =
            typeof response?.meta?.pagination?.hasMore === 'boolean'
              ? response.meta.pagination.hasMore
              : undefined;
          const directHasMore =
            typeof response?.hasMore === 'boolean' ? response.hasMore : undefined;
          return {
            data: rows,
            total: typeof response.total === 'number' ? response.total : rows.length,
            hasMore: metaHasMore ?? directHasMore,
            page:
              typeof response?.meta?.pagination?.page === 'number'
                ? response.meta.pagination.page
                : typeof response?.page === 'number'
                  ? response.page
                  : undefined,
            limit:
              typeof response?.meta?.pagination?.limit === 'number'
                ? response.meta.pagination.limit
                : typeof response?.limit === 'number'
                  ? response.limit
                  : undefined,
          };
        }
        if (Array.isArray(response)) {
          const rows = response.filter(isRecord);
          return { data: rows, total: rows.length, hasMore: false };
        }
        return { data: [], total: 0, hasMore: false };
      },
      providesTags: ['Prompts'],
    }),

    getPrompt: builder.query<PromptListItem, string>({
      query: (promptId) => projectScopedPath(`/prompts/${promptId}`, getCurrentProjectId()),
      providesTags: (result, error, promptId) => [{ type: 'Prompts', id: promptId }],
    }),

    createPrompt: builder.mutation<
      PromptListItem,
      { promptId: string; name: string; description?: string; intent?: string; tags?: string[]; modelCode?: string; fallbackModelCodes?: string[]; outputTemplate?: string; inputParameters?: any[]; modelParameters?: Record<string, any>; sourceScenarioId?: string; sourceExecutionGroupId?: string; status?: PromptStatus }
    >({
      query: (body) => ({
        url: projectScopedPath('/prompts', getCurrentProjectId()),
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Prompts'],
    }),

    updatePrompt: builder.mutation<
      PromptListItem,
      { promptId: string; name?: string; description?: string; intent?: string; tags?: string[]; modelCode?: string; fallbackModelCodes?: string[]; outputTemplate?: string; inputParameters?: any[]; modelParameters?: Record<string, any>; sourceScenarioId?: string; sourceExecutionGroupId?: string; status?: PromptStatus }
    >({
      query: ({ promptId, ...body }) => ({
        url: projectScopedPath(`/prompts/${promptId}`, getCurrentProjectId()),
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (result, error, { promptId }) => [
        { type: 'Prompts', id: promptId },
        'Prompts',
      ],
    }),

    deletePrompt: builder.mutation<{ success: boolean }, string>({
      query: (promptId) => ({
        url: projectScopedPath(`/prompts/${promptId}`, getCurrentProjectId()),
        method: 'DELETE',
      }),
      invalidatesTags: ['Prompts', 'Versions', 'Releases'],
    }),

    // ─── Flows (Phase 1 — see ROADMAP-flows.md) ─────────────────────────
    listFlows: builder.query<
      { data: FlowListItem[]; total: number; hasMore?: boolean; page?: number; limit?: number },
      { q?: string; tag?: string; page?: number; limit?: number } | void
    >({
      query: (params) => ({
        url: projectScopedPath('/flows', getCurrentProjectId()),
        method: 'GET',
        params: params || {},
      }),
      transformResponse: (response: any) => {
        const rows = Array.isArray(response?.data) ? response.data : [];
        return {
          data: rows,
          total: typeof response?.total === 'number' ? response.total : rows.length,
          hasMore: response?.hasMore,
          page: response?.page,
          limit: response?.limit,
        };
      },
      providesTags: ['Flows'],
    }),

    getFlow: builder.query<FlowListItem, string>({
      query: (flowId) => projectScopedPath(`/flows/${flowId}`, getCurrentProjectId()),
      providesTags: (result, error, flowId) => [{ type: 'Flows', id: flowId }],
    }),

    createFlow: builder.mutation<
      FlowListItem,
      {
        flowId: string;
        name: string;
        description?: string;
        tags?: string[];
        status?: 'active' | 'paused' | 'inactive';
      }
    >({
      query: (body) => ({
        url: projectScopedPath('/flows', getCurrentProjectId()),
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Flows'],
    }),

    updateFlow: builder.mutation<
      FlowListItem,
      {
        flowId: string;
        name?: string;
        description?: string;
        tags?: string[];
        status?: 'active' | 'paused' | 'inactive';
      }
    >({
      query: ({ flowId, ...body }) => ({
        url: projectScopedPath(`/flows/${flowId}`, getCurrentProjectId()),
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (result, error, { flowId }) => [{ type: 'Flows', id: flowId }, 'Flows'],
    }),

    deleteFlow: builder.mutation<{ success: boolean }, string>({
      query: (flowId) => ({
        url: projectScopedPath(`/flows/${flowId}`, getCurrentProjectId()),
        method: 'DELETE',
      }),
      invalidatesTags: ['Flows', 'FlowVersions'],
    }),

    listFlowVersions: builder.query<FlowVersionRecord[], string>({
      query: (flowId) => projectScopedPath(`/flows/${flowId}/versions`, getCurrentProjectId()),
      transformResponse: (response: any) =>
        Array.isArray(response?.data) ? response.data : Array.isArray(response) ? response : [],
      providesTags: (result, error, flowId) => [{ type: 'FlowVersions', id: flowId }],
    }),

    getFlowVersion: builder.query<FlowVersionRecord, { flowId: string; version: string }>({
      query: ({ flowId, version }) =>
        projectScopedPath(`/flows/${flowId}/versions/${version}`, getCurrentProjectId()),
    }),

    createFlowVersion: builder.mutation<
      FlowVersionRecord,
      { flowId: string; spec: any; version?: string; changelog?: string; activate?: boolean }
    >({
      query: ({ flowId, ...body }) => ({
        url: projectScopedPath(`/flows/${flowId}/versions`, getCurrentProjectId()),
        method: 'POST',
        body,
      }),
      invalidatesTags: (result, error, { flowId }) => [
        { type: 'Flows', id: flowId },
        { type: 'FlowVersions', id: flowId },
        'Flows',
      ],
    }),

    activateFlowVersion: builder.mutation<
      FlowVersionRecord,
      { flowId: string; version: string }
    >({
      query: ({ flowId, version }) => ({
        url: projectScopedPath(
          `/flows/${flowId}/versions/${version}/activate`,
          getCurrentProjectId(),
        ),
        method: 'POST',
      }),
      invalidatesTags: (result, error, { flowId }) => [
        { type: 'Flows', id: flowId },
        { type: 'FlowVersions', id: flowId },
        'Flows',
      ],
    }),

    // Versions
    listVersions: builder.query<PromptVersion[], string>({
      query: (promptId) =>
        projectScopedPath(`/prompts/${promptId}/versions`, getCurrentProjectId()),
      transformResponse: (response: any) =>
        (Array.isArray(response?.data)
          ? response.data
          : Array.isArray(response)
            ? response
            : []
        ).filter(isRecord) as PromptVersion[],
      providesTags: (result, error, promptId) => [
        { type: 'Versions', id: promptId },
      ],
    }),

    getVersion: builder.query<PromptVersion, { promptId: string; version: string }>({
      query: ({ promptId, version }) =>
        projectScopedPath(`/prompts/${promptId}/versions/${version}`, getCurrentProjectId()),
      providesTags: (result, error, { promptId, version }) => [
        { type: 'Versions', id: `${promptId}-${version}` },
      ],
    }),

    createVersion: builder.mutation<
      PromptVersion,
      { promptId: string; spec: PromptSpec; version?: string; createdBy?: string; changelog?: string; activate?: boolean; sourceScenarioId?: string; sourceExecutionGroupId?: string }
    >({
      query: ({ promptId, ...body }) => ({
        url: projectScopedPath(`/prompts/${promptId}/versions`, getCurrentProjectId()),
        method: 'POST',
        body,
      }),
      invalidatesTags: (result, error, { promptId }) => [
        { type: 'Versions', id: promptId },
        'Prompts',
      ],
    }),

    deleteVersion: builder.mutation<
      { success: boolean },
      { promptId: string; version: string }
    >({
      query: ({ promptId, version }) => ({
        url: projectScopedPath(`/prompts/${promptId}/versions/${version}`, getCurrentProjectId()),
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { promptId }) => [
        { type: 'Versions', id: promptId },
        'Prompts',
      ],
    }),

    setActiveVersion: builder.mutation<
      PromptVersion,
      { promptId: string; version: string }
    >({
      query: ({ promptId, version }) => ({
        url: projectScopedPath(
          `/prompts/${promptId}/versions/${version}/active`,
          getCurrentProjectId(),
        ),
        method: 'PATCH',
      }),
      invalidatesTags: (result, error, { promptId }) => [
        { type: 'Versions', id: promptId },
        'Prompts',
      ],
    }),

    // Releases
    listReleases: builder.query<PromptRelease[], string>({
      query: (promptId) =>
        projectScopedPath(`/prompts/${promptId}/releases`, getCurrentProjectId()),
      transformResponse: (response: any) =>
        (Array.isArray(response?.data)
          ? response.data
          : Array.isArray(response)
            ? response
            : []
        ).filter(isRecord) as PromptRelease[],
      providesTags: (result, error, promptId) => [
        { type: 'Releases', id: promptId },
      ],
    }),

    setRelease: builder.mutation<
      PromptRelease,
      { promptId: string; channel: string; version: string }
    >({
      query: ({ promptId, channel, ...body }) => ({
        url: projectScopedPath(`/prompts/${promptId}/releases/${channel}`, getCurrentProjectId()),
        method: 'PUT',
        body,
      }),
      invalidatesTags: (result, error, { promptId }) => [
        { type: 'Releases', id: promptId },
      ],
    }),

    // Bundles
    exportBundle: builder.query<
      any,
      { promptIds?: string[]; includeTestcases?: boolean }
    >({
      query: (params) => {
        const searchParams = new URLSearchParams();
        if (params.promptIds) {
          searchParams.append('promptIds', params.promptIds.join(','));
        }
        if (params.includeTestcases) {
          searchParams.append('includeTestcases', 'true');
        }
        return {
          url: `${projectScopedPath('/bundles/export', getCurrentProjectId())}${searchParams.toString() ? `?${searchParams.toString()}` : ''}`,
          method: 'GET',
        };
      },
    }),

    importBundle: builder.mutation<
      { imported: number; skipped: number; errors: any[] },
      { bundle: any; options?: { mode?: 'merge' | 'overwrite'; allowNewPromptIds?: boolean } }
    >({
      query: ({ bundle, options }) => ({
        url: projectScopedPath('/bundles/import', getCurrentProjectId()),
        method: 'POST',
        body: { bundle, options },
      }),
      invalidatesTags: ['Prompts', 'Versions', 'Releases'],
    }),

    // Interactions
    listInteractions: builder.query<
      {
        data: InteractionListItem[];
        total: number;
        hasMore?: boolean;
        limit?: number;
        offset?: number;
      },
      {
        promptId?: string;
        providerType?: string;
        modelId?: string;
        status?: string;
        startDate?: string;
        endDate?: string;
        limit?: number;
        offset?: number;
        skip?: number; // backward compatibility
        orderBy?: string;
        orderDir?: 'asc' | 'desc';
      }
    >({
      query: (params) => ({
        url: projectScopedPath('/interactions', getCurrentProjectId()),
        method: 'GET',
        params,
      }),
      transformResponse: (response: any) => {
        const envelopeData = Array.isArray(response?.data) ? response.data : Array.isArray(response) ? response : [];
        const normalized = envelopeData.filter(isRecord).map(normalizeInteraction);
        const total = typeof response?.total === 'number' ? response.total : normalized.length;
        const meta = response?.meta;
        const limit =
          typeof meta?.pagination?.limit === 'number'
            ? meta.pagination.limit
            : typeof meta?.limit === 'number'
              ? meta.limit
              : undefined;
        const offset =
          typeof meta?.pagination?.offset === 'number'
            ? meta.pagination.offset
            : typeof meta?.offset === 'number'
              ? meta.offset
              : undefined;
        const metaHasMore =
          typeof meta?.pagination?.hasMore === 'boolean'
            ? meta.pagination.hasMore
            : typeof meta?.hasMore === 'boolean'
              ? meta.hasMore
              : undefined;
        const directHasMore =
          typeof response?.hasMore === 'boolean'
            ? response.hasMore
            : undefined;
        const computedHasMore =
          typeof total === 'number' && typeof limit === 'number' && typeof offset === 'number'
            ? offset + normalized.length < total
            : undefined;
        return {
          data: normalized,
          total,
          hasMore: metaHasMore ?? directHasMore ?? computedHasMore,
          limit,
          offset,
        };
      },
      providesTags: ['Interactions'],
    }),

    listAlertLogs: builder.query<
      { data: AlertLogListItem[]; total: number },
      {
        q?: string;
        promptId?: string;
        severity?: string;
        type?: string;
        limit?: number;
        offset?: number;
        skip?: number;
        orderBy?: string;
        orderDir?: 'asc' | 'desc';
      }
    >({
      query: (params) => ({
        url: projectScopedPath('/alert-logs', getCurrentProjectId()),
        method: 'GET',
        params,
      }),
      transformResponse: (response: any) => {
        const envelopeData = Array.isArray(response?.data)
          ? response.data
          : Array.isArray(response)
            ? response
            : [];
        const normalized = envelopeData.filter(isRecord).map(normalizeAlertLog);
        return {
          data: normalized,
          total: typeof response?.total === 'number' ? response.total : normalized.length,
        };
      },
      providesTags: ['AlertLogs'],
    }),

    getInteractionAnalytics: builder.query<
      InteractionAnalyticsResponse,
      {
        startDate?: string;
        endDate?: string;
        status?: string;
        providerType?: string;
        modelId?: string;
        bucket?: 'hour' | 'day';
      }
    >({
      query: (params) => ({
        url: projectScopedPath('/interactions/analytics/summary', getCurrentProjectId()),
        method: 'GET',
        params,
      }),
      transformResponse: (response: any) => parseInteractionAnalyticsResponse(response),
    }),

    getInteractionStatusBreakdown: builder.query<
      {
        data: Array<{ status: string; count: number }>;
        total: number;
        meta: { startDate?: string; endDate?: string; generatedAtUtc: string };
      },
      {
        startDate?: string;
        endDate?: string;
        providerType?: string;
        modelId?: string;
        promptId?: string;
      } | void
    >({
      query: (params) => ({
        url: projectScopedPath('/interactions/analytics/status', getCurrentProjectId()),
        method: 'GET',
        params: params || {},
      }),
      transformResponse: (response: any) => {
        const rows = Array.isArray(response?.data) ? response.data : [];
        const data = rows
          .filter(isRecord)
          .map((row: any) => ({
            status: typeof row.status === 'string' ? row.status : 'unknown',
            count: Number(row.count) || 0,
          }))
          .filter((row: { count: number }) => row.count > 0);
        return {
          data,
          total: typeof response?.total === 'number' ? response.total : data.reduce((acc: number, r: any) => acc + r.count, 0),
          meta: {
            startDate: response?.meta?.startDate,
            endDate: response?.meta?.endDate,
            generatedAtUtc: response?.meta?.generatedAtUtc || new Date().toISOString(),
          },
        };
      },
    }),

    getCacheSavings: builder.query<
      {
        windowDays: number;
        totalExecutions: number;
        uniqueExecutions: number;
        duplicates: number;
        duplicateRate: number;
        totalCost: number;
        estimatedCostSaved: number;
        currency: string;
      },
      { windowDays?: number; providerType?: string; modelId?: string } | void
    >({
      query: (params) => ({
        url: projectScopedPath('/interactions/analytics/cache-savings', getCurrentProjectId()),
        method: 'GET',
        params: params || {},
      }),
    }),

    getInteraction: builder.query<InteractionDetail, string>({
      query: (interactionId) =>
        projectScopedPath(`/interactions/${interactionId}`, getCurrentProjectId()),
      transformResponse: (response: any) => {
        const source = extractInteractionPayload(response);
        const normalized = normalizeInteraction(source);
        return {
          ...source,
          ...normalized,
          inputs: source.inputs,
          inputFiles: source.inputFiles ?? source.input_files,
          outputText: source.outputText ?? source.output_text,
          outputJson: source.outputJson ?? source.output_json,
          generatedMedia: source.generatedMedia ?? source.generated_media,
          queueResponses: source.queueResponses ?? source.queue_responses,
          rawResponse: source.rawResponse ?? source.raw_response,
          providerMetadata: source.providerMetadata,
          evalResults: source.evalResults,
          error: source.error,
        };
      },
    }),

    // ─── Project Model Library ─────────────────────────────────────────────

    listProjectModels: builder.query<
      { data: ProjectModel[]; total: number },
      { q?: string; modality?: string; status?: string; providerType?: string } | void
    >({
      query: (params) => ({
        url: projectScopedPath('/project-models', getCurrentProjectId()),
        method: 'GET',
        params: params || {},
      }),
      providesTags: ['ProjectModels'],
    }),

    fetchLiveModels: builder.query<
      { data: LiveModelOption[]; total: number; provider: string },
      { provider: string; q?: string }
    >({
      query: ({ provider, q }) => ({
        url: '/project-models/providers/live',
        method: 'GET',
        params: { provider, q },
      }),
    }),

    createProjectModel: builder.mutation<ProjectModel, CreateProjectModelPayload>({
      query: (body) => ({
        url: projectScopedPath('/project-models', getCurrentProjectId()),
        method: 'POST',
        body,
      }),
      invalidatesTags: ['ProjectModels'],
    }),

    updateProjectModel: builder.mutation<ProjectModel, { id: string } & Partial<CreateProjectModelPayload>>({
      query: ({ id, ...body }) => ({
        url: projectScopedPath(`/project-models/${id}`, getCurrentProjectId()),
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['ProjectModels'],
    }),

    deleteProjectModel: builder.mutation<{ success: boolean }, string>({
      query: (id) => ({
        url: projectScopedPath(`/project-models/${id}`, getCurrentProjectId()),
        method: 'DELETE',
      }),
      invalidatesTags: ['ProjectModels'],
    }),

    // ─── Lab Assistant ─────────────────────────────────────────────────
    // PromptLab's internal AI execution layer that powers the five Prompt
    // Studio surfaces (Compose with AI, Optimize, inline coach hints,
    // Translate for this model, Explain this run). All endpoints proxy
    // through registry to a dedicated `lab-assistant` PromptLab project —
    // frontend never touches that project's API key directly. See
    // promptlab-registry-service/docs/ROADMAP.md "Step 0 — Lab Assistant".

    getLabAssistantStatus: builder.query<LabAssistantStatus, void>({
      query: () => ({
        url: '/registry/lab-assistant/status',
        method: 'GET',
      }),
    }),

    // Cost-preview (Phase B.3). Cached per operation+model so the same
    // CostPreview badge across multiple buttons doesn't refetch on every
    // mount. No auto-refetch — the underlying p10/p90 distribution moves
    // slowly enough that a stale-while-revalidate-on-mount strategy is
    // fine, and we don't want every action button hammering the registry.
    getLabAssistantCostEstimate: builder.query<CostEstimateResponse, CostEstimateRequest>({
      query: ({ operation, model }) => ({
        url: '/registry/lab-assistant/cost-estimate',
        method: 'GET',
        params: model ? { operation, model } : { operation },
      }),
    }),

    runMetaprompt: builder.mutation<LabAssistantResponse, MetapromptRequest>({
      query: (body) => ({
        url: '/registry/lab-assistant/metaprompt',
        method: 'POST',
        body,
      }),
    }),

    runCoach: builder.mutation<LabAssistantResponse, CoachRequest>({
      query: (body) => ({
        url: '/registry/lab-assistant/coach',
        method: 'POST',
        body,
      }),
    }),

    runOptimize: builder.mutation<LabAssistantResponse, OptimizeRequest>({
      query: (body) => ({
        url: '/registry/lab-assistant/optimize',
        method: 'POST',
        body,
      }),
    }),

    runOptimizeForCohort: builder.mutation<LabAssistantResponse, OptimizeForCohortRequest>({
      query: (body) => ({
        url: '/registry/lab-assistant/optimize-for-cohort',
        method: 'POST',
        body,
      }),
    }),

    runRefine: builder.mutation<LabAssistantResponse, RefineRequest>({
      query: (body) => ({
        url: '/registry/lab-assistant/refine',
        method: 'POST',
        body,
      }),
    }),

    runTranslateSyntax: builder.mutation<LabAssistantResponse, TranslateSyntaxRequest>({
      query: (body) => ({
        url: '/registry/lab-assistant/translate-syntax',
        method: 'POST',
        body,
      }),
    }),

    runVary: builder.mutation<LabAssistantResponse, VaryRequest>({
      query: (body) => ({
        url: '/registry/lab-assistant/vary',
        method: 'POST',
        body,
      }),
    }),

    runExplain: builder.mutation<LabAssistantResponse, ExplainRequest>({
      query: (body) => ({
        url: '/registry/lab-assistant/explain',
        method: 'POST',
        body,
      }),
    }),

    // Suggest a short title from prompt or scenario content. Powers the
    // "Suggest name" affordance in the Save Prompt / Save Scenario
    // dialogs.
    runAutoName: builder.mutation<LabAssistantResponse, AutoNameRequest>({
      query: (body) => ({
        url: '/registry/lab-assistant/auto-name',
        method: 'POST',
        body,
      }),
    }),

    // Suggest tags for new content. Powers the "Suggest tags"
    // affordance in the Save Prompt / Save Scenario dialogs.
    runAutoTag: builder.mutation<LabAssistantResponse, AutoTagRequest>({
      query: (body) => ({
        url: '/registry/lab-assistant/auto-tag',
        method: 'POST',
        body,
      }),
    }),

    // Record thumbs-up / thumbs-down on a Lab Assistant suggestion.
    // Fire-and-forget — never blocks the surface and never surfaces
    // an error toast (rating telemetry is opt-in for the user, the
    // worst-case "we missed a rating" doesn't warrant interrupting
    // the flow).
    rateLabAssistantSuggestion: builder.mutation<
      RateLabAssistantSuggestionResponse,
      RateLabAssistantSuggestionRequest
    >({
      query: (body) => ({
        url: '/registry/lab-assistant/rate',
        method: 'POST',
        body,
      }),
    }),
  }),
});

// Router API (for execution)
export const routerApi = createApi({
  reducerPath: 'routerApi',
  baseQuery: baseQueryRouterWithReauth,
  tagTypes: ['Interactions', 'RuntimeModels', 'MediaJobs', 'ExecutionBatches', 'FlowRuns'],
  endpoints: (builder) => ({
    executePrompt: builder.mutation<ExecutionResult, ExecutionRequest>({
      query: (body) => {
        const projectId = getCurrentProjectId();
        const requestBody: ExecutionRequest = projectId
          ? {
              ...body,
              context: {
                ...(body.context || {}),
                projectId,
              },
            }
          : body;
        return {
          url: routerProjectScopedPath('/execute', projectId || body.context?.projectId),
          method: 'POST',
          body: requestBody,
        };
      },
      invalidatesTags: ['Interactions'],
    }),

    listProviders: builder.query<any[], void>({
      query: () => ({
        url: routerProjectScopedPath('/providers', getCurrentProjectId()),
        method: 'GET',
      }),
    }),

    listRuntimeModels: builder.query<
      { generatedAt: string; total: number; models: RuntimeModel[] },
      void
    >({
      query: () => ({
        url: `${routerApiPathPrefix}/providers/models`,
        method: 'GET',
      }),
      transformResponse: (response: any) => normalizeRuntimeModelsResponse(response),
      providesTags: ['RuntimeModels'],
    }),

    testProvider: builder.mutation<
      { success: boolean; error?: string },
      { type: string; credentials?: any }
    >({
      query: ({ type, credentials }) => ({
        url: routerProjectScopedPath(`/providers/${type}/test`, getCurrentProjectId()),
        method: 'POST',
        body: { credentials },
      }),
    }),

    submitMediaExecution: builder.mutation<MediaExecutionAccepted, MediaExecutionRequest>({
      async queryFn(request) {
        try {
          const projectId = getCurrentProjectId() || request.context?.projectId?.toString();
          const formData = new FormData();
          formData.set(
            'payload',
            JSON.stringify({
              providerType: request.providerType,
              model: request.model,
              prompt: request.prompt,
              promptId: request.promptId,
              promptVersion: request.promptVersion,
              promptName: request.promptName,
              accountId: request.accountId,
              input: request.input,
              options: request.options,
              context: request.context,
              trace: request.trace,
              fileDescriptors: (request.files || []).map((entry) => ({
                fieldName: entry.fieldName,
              })),
            }),
          );

          for (const entry of request.files || []) {
            formData.append('files', entry.file, entry.file.name);
          }

          const response = await fetch(
            `${routerServiceURL}${routerProjectScopedPath('/media/execute', projectId)}`,
            {
              method: 'POST',
              credentials: 'include',
              headers: buildRouterHeaders(),
              body: formData,
            },
          );

          const data = await response.json().catch(() => null);
          if (!response.ok) {
            return {
              error: {
                status: response.status,
                data,
              } as any,
            };
          }

          return { data: data as MediaExecutionAccepted };
        } catch (error: any) {
          return {
            error: {
              status: 'FETCH_ERROR',
              error: error?.message || 'Failed to submit media execution',
            } as any,
          };
        }
      },
      invalidatesTags: ['MediaJobs', 'Interactions'],
    }),

    getMediaExecutionStatus: builder.query<MediaExecutionStatus, { jobId: string }>({
      query: ({ jobId }) => ({
        url: routerProjectScopedPath(`/media/jobs/${jobId}`, getCurrentProjectId()),
        method: 'GET',
      }),
      providesTags: (result, error, { jobId }) => [{ type: 'MediaJobs', id: jobId }],
    }),

    getMediaExecutionResult: builder.query<MediaExecutionResult, { jobId: string }>({
      query: ({ jobId }) => ({
        url: routerProjectScopedPath(`/media/jobs/${jobId}/result`, getCurrentProjectId()),
        method: 'GET',
      }),
      providesTags: (result, error, { jobId }) => [{ type: 'MediaJobs', id: `${jobId}-result` }],
    }),

    cancelMediaExecution: builder.mutation<MediaExecutionStatus, { jobId: string }>({
      query: ({ jobId }) => ({
        url: routerProjectScopedPath(`/media/jobs/${jobId}/cancel`, getCurrentProjectId()),
        method: 'POST',
      }),
      invalidatesTags: (result, error, { jobId }) => [
        { type: 'MediaJobs', id: jobId },
        { type: 'Interactions' },
      ],
    }),

    publishShare: builder.mutation<
      { shareId: string; url: string },
      { snapshot: any }
    >({
      async queryFn(request) {
        try {
          const projectId = getCurrentProjectId();
          const response = await fetch(
            `${routerServiceURL}${routerProjectScopedPath('/media/share', projectId)}`,
            {
              method: 'POST',
              credentials: 'include',
              headers: { ...buildRouterHeaders(), 'Content-Type': 'application/json' },
              body: JSON.stringify({ snapshot: request.snapshot }),
            },
          );
          const data = await response.json().catch(() => null);
          if (!response.ok) {
            return { error: { status: response.status, data } as any };
          }
          return { data: data as { shareId: string; url: string } };
        } catch (error: any) {
          return {
            error: { status: 'FETCH_ERROR', error: error?.message || 'Failed to publish share' } as any,
          };
        }
      },
    }),

    createExecutionBatch: builder.mutation<
      ExecutionBatch,
      {
        scenarioId?: string;
        promptId?: string;
        version?: string;
        shared: {
          prompt: string;
          promptParameters?: Record<string, any>;
          modelParameters?: Record<string, any>;
          outputTemplate?: string;
          inputAssets?: ScenarioAsset[];
        };
        models: ScenarioModelRef[];
        context?: Record<string, any>;
        trace?: Record<string, any>;
        files?: Array<{
          fieldName: string;
          file: File;
        }>;
      }
    >({
      async queryFn(request) {
        try {
          const projectId = getCurrentProjectId() || request.context?.projectId?.toString();
          const requestBody = {
            ...request,
            context: projectId
              ? {
                  ...(request.context || {}),
                  projectId,
                }
              : request.context,
          };

          if (request.files?.length) {
            const formData = new FormData();
            formData.set(
              'payload',
              JSON.stringify({
                scenarioId: requestBody.scenarioId,
                promptId: requestBody.promptId,
                version: requestBody.version,
                shared: requestBody.shared,
                models: requestBody.models,
                context: requestBody.context,
                trace: requestBody.trace,
                fileDescriptors: request.files.map((entry) => ({
                  fieldName: entry.fieldName,
                })),
              }),
            );
            for (const entry of request.files) {
              formData.append('files', entry.file, entry.file.name);
            }

            const response = await fetch(
              `${routerServiceURL}${routerProjectScopedPath('/execute/batches', projectId)}`,
              {
                method: 'POST',
                credentials: 'include',
                headers: buildRouterHeaders(),
                body: formData,
              },
            );

            const data = await response.json().catch(() => null);
            if (!response.ok) {
              return {
                error: {
                  status: response.status,
                  data,
                } as any,
              };
            }

            return { data: data as ExecutionBatch };
          }

          const response = await fetch(
            `${routerServiceURL}${routerProjectScopedPath('/execute/batches', projectId)}`,
            {
              method: 'POST',
              credentials: 'include',
              headers: {
                ...buildRouterHeaders(),
                'Content-Type': 'application/json',
              },
              body: JSON.stringify(requestBody),
            },
          );

          const data = await response.json().catch(() => null);
          if (!response.ok) {
            return {
              error: {
                status: response.status,
                data,
              } as any,
            };
          }

          return { data: data as ExecutionBatch };
        } catch (error: any) {
          return {
            error: {
              status: 'FETCH_ERROR',
              error: error?.message || 'Failed to create execution batch',
            } as any,
          };
        }
      },
      invalidatesTags: ['ExecutionBatches', 'Interactions'],
    }),

    getExecutionBatch: builder.query<ExecutionBatch, { executionGroupId: string }>({
      query: ({ executionGroupId }) => ({
        url: routerProjectScopedPath(`/execute/batches/${executionGroupId}`, getCurrentProjectId()),
        method: 'GET',
      }),
      providesTags: (result, error, { executionGroupId }) => [
        { type: 'ExecutionBatches', id: executionGroupId },
      ],
    }),

    getExecutionJob: builder.query<ExecutionJob, { jobId: string }>({
      query: ({ jobId }) => ({
        url: routerProjectScopedPath(`/execute/jobs/${jobId}`, getCurrentProjectId()),
        method: 'GET',
      }),
      providesTags: (result, error, { jobId }) => [{ type: 'ExecutionBatches', id: jobId }],
    }),

    cancelExecutionBatch: builder.mutation<ExecutionBatch, { executionGroupId: string }>({
      query: ({ executionGroupId }) => ({
        url: routerProjectScopedPath(
          `/execute/batches/${executionGroupId}/cancel`,
          getCurrentProjectId(),
        ),
        method: 'POST',
      }),
      invalidatesTags: (result, error, { executionGroupId }) => [
        { type: 'ExecutionBatches', id: executionGroupId },
        'Interactions',
      ],
    }),

    cancelExecutionJob: builder.mutation<ExecutionJob, { jobId: string }>({
      query: ({ jobId }) => ({
        url: routerProjectScopedPath(`/execute/jobs/${jobId}/cancel`, getCurrentProjectId()),
        method: 'POST',
      }),
      invalidatesTags: (result, error, { jobId }) => [
        { type: 'ExecutionBatches', id: jobId },
        'Interactions',
      ],
    }),

    // ─── Flow Runs (Phase 1 — see ROADMAP-flows.md) ─────────────────────
    startFlowRun: builder.mutation<
      FlowRunRecord,
      { flowId: string; inputs: Record<string, any>; options?: { version?: string; bypassCache?: boolean } }
    >({
      query: ({ flowId, inputs, options }) => ({
        url: routerProjectScopedPath(`/flows/${flowId}/runs`, getCurrentProjectId()),
        method: 'POST',
        body: { inputs, options },
      }),
      invalidatesTags: ['FlowRuns'],
    }),

    getFlowRun: builder.query<FlowRunRecord, string>({
      query: (flowRunId) =>
        routerProjectScopedPath(`/flows/runs/${flowRunId}`, getCurrentProjectId()),
      providesTags: (result, error, flowRunId) => [{ type: 'FlowRuns', id: flowRunId }],
    }),

    listFlowRuns: builder.query<
      { data: FlowRunRecord[]; total: number },
      { flowId?: string; status?: string; limit?: number; offset?: number } | void
    >({
      query: (params) => ({
        url: routerProjectScopedPath('/flows/runs', getCurrentProjectId()),
        method: 'GET',
        params: params || {},
      }),
      providesTags: ['FlowRuns'],
    }),

    cancelFlowRun: builder.mutation<FlowRunRecord, { flowRunId: string }>({
      query: ({ flowRunId }) => ({
        url: routerProjectScopedPath(`/flows/runs/${flowRunId}/cancel`, getCurrentProjectId()),
        method: 'POST',
      }),
      invalidatesTags: (result, error, { flowRunId }) => [
        { type: 'FlowRuns', id: flowRunId },
        'FlowRuns',
      ],
    }),
  }),
});

export const {
  useListProjectsQuery,
  useCreateProjectMutation,
  useUpdateProjectMutation,
  useDeleteProjectMutation,
  useListProjectApiKeysQuery,
  useCreateProjectApiKeyMutation,
  useRevokeProjectApiKeyMutation,
  useListModelRegistryRecordsQuery,
  useCreateModelRegistryRecordMutation,
  useUpdateModelRegistryRecordMutation,
  useGetProjectAccessQuery,
  useInviteProjectUserMutation,
  useUpdateProjectMemberRoleMutation,
  useRevokeProjectMemberMutation,
  useRevokeProjectInvitationMutation,
  useListMyProjectInvitationsQuery,
  useAcceptMyProjectInvitationMutation,
  useDeclineMyProjectInvitationMutation,
  useListPromptsQuery,
  useGetPromptQuery,
  useCreatePromptMutation,
  useUpdatePromptMutation,
  useDeletePromptMutation,
  useListFlowsQuery,
  useGetFlowQuery,
  useCreateFlowMutation,
  useUpdateFlowMutation,
  useDeleteFlowMutation,
  useListFlowVersionsQuery,
  useGetFlowVersionQuery,
  useCreateFlowVersionMutation,
  useActivateFlowVersionMutation,
  useListVersionsQuery,
  useGetVersionQuery,
  useCreateVersionMutation,
  useDeleteVersionMutation,
  useSetActiveVersionMutation,
  useListReleasesQuery,
  useSetReleaseMutation,
  useExportBundleQuery,
  useImportBundleMutation,
  useListInteractionsQuery,
  useLazyListInteractionsQuery,
  useListAlertLogsQuery,
  useLazyListAlertLogsQuery,
  useGetInteractionAnalyticsQuery,
  useGetInteractionStatusBreakdownQuery,
  useGetCacheSavingsQuery,
  useGetInteractionQuery,
  useListProviderAccountsQuery,
  useGetModelFavoritesQuery,
  useGetModelSyncStatusQuery,
  useRunModelSyncMutation,
  useRunAllModelSyncMutation,
  useUpsertModelFavoriteMutation,
  useDeleteModelFavoriteMutation,
  useCreateProviderAccountMutation,
  useUpdateProviderAccountMutation,
  useDeleteProviderAccountMutation,
  useListScenariosQuery,
  useGetScenarioQuery,
  useCreateScenarioMutation,
  useUpdateScenarioMutation,
  useAppendLabAssistantEventMutation,
  useListProjectModelsQuery,
  useFetchLiveModelsQuery,
  useLazyFetchLiveModelsQuery,
  useCreateProjectModelMutation,
  useUpdateProjectModelMutation,
  useDeleteProjectModelMutation,
  // Lab Assistant
  useGetLabAssistantStatusQuery,
  useLazyGetLabAssistantStatusQuery,
  useGetLabAssistantCostEstimateQuery,
  useLazyGetLabAssistantCostEstimateQuery,
  useRunMetapromptMutation,
  useRunCoachMutation,
  useRunOptimizeMutation,
  useRunOptimizeForCohortMutation,
  useRunRefineMutation,
  useRunTranslateSyntaxMutation,
  useRunVaryMutation,
  useRunExplainMutation,
  useRunAutoNameMutation,
  useRunAutoTagMutation,
  useRateLabAssistantSuggestionMutation,
} = registryApi;

export const {
  useExecutePromptMutation,
  useSubmitMediaExecutionMutation,
  useLazyGetMediaExecutionStatusQuery,
  useLazyGetMediaExecutionResultQuery,
  useCancelMediaExecutionMutation,
  useListProvidersQuery,
  useListRuntimeModelsQuery,
  useTestProviderMutation,
  useCreateExecutionBatchMutation,
  usePublishShareMutation,
  useLazyGetExecutionBatchQuery,
  useLazyGetExecutionJobQuery,
  useCancelExecutionBatchMutation,
  useCancelExecutionJobMutation,
  useStartFlowRunMutation,
  useGetFlowRunQuery,
  useLazyGetFlowRunQuery,
  useListFlowRunsQuery,
  useCancelFlowRunMutation,
} = routerApi;
